import type { OrdersParseTree } from "../parser";
import type { ScheduledSpeech } from "../../../services/speech";

export type DeliveryStatus = "complete" | "idle" | "paused" | "playing";

export interface DeliveryState {
  readonly cursor: number;
  readonly lastSpokenIndex: number | null;
  readonly status: DeliveryStatus;
  readonly targetWpm: number;
}

export type DeliveryAction =
  | { readonly type: "FINISH_ENTRY" }
  | { readonly type: "JUMP"; readonly paragraph: number }
  | { readonly type: "NEXT" }
  | { readonly type: "NUDGE_RATE"; readonly delta: number }
  | { readonly type: "PAUSE" }
  | { readonly type: "PLAY" }
  | { readonly type: "PREVIOUS" }
  | { readonly type: "STOP" };

export interface DeliveryDisplayNode {
  readonly id: string;
  readonly kind: "item" | "paragraph-heading" | "subparagraph-heading";
  readonly text: string;
}

export interface DeliveryDisplayState extends DeliveryDisplayNode {
  readonly state: "current" | "delivered" | "undelivered";
}

export type DeliveryKeyboardAction = Extract<
  DeliveryAction,
  {
    readonly type:
      | "JUMP"
      | "NEXT"
      | "NUDGE_RATE"
      | "PLAY"
      | "PREVIOUS"
      | "STOP";
  }
>;

export function createDeliveryState(targetWpm: number): DeliveryState {
  if (!Number.isFinite(targetWpm) || targetWpm < 80 || targetWpm > 180) {
    throw new RangeError("delivery target must be 80 to 180 wpm");
  }
  return {
    cursor: 0,
    lastSpokenIndex: null,
    status: "idle",
    targetWpm,
  };
}

function boundedCursor(cursor: number, length: number): number {
  if (length === 0) return 0;
  return Math.max(0, Math.min(cursor, length - 1));
}

function paragraphStart(
  schedule: readonly ScheduledSpeech[],
  cursor: number,
): number {
  for (
    let index = boundedCursor(cursor, schedule.length);
    index >= 0;
    index -= 1
  ) {
    if (schedule[index]?.kind === "paragraph-heading") return index;
  }
  return 0;
}

function adjacentItem(
  schedule: readonly ScheduledSpeech[],
  cursor: number,
  direction: -1 | 1,
): number {
  for (
    let index = boundedCursor(cursor, schedule.length) + direction;
    index >= 0 && index < schedule.length;
    index += direction
  ) {
    if (schedule[index]?.kind === "item") return index;
  }
  return boundedCursor(cursor, schedule.length);
}

export function transitionDelivery(
  state: DeliveryState,
  action: DeliveryAction,
  schedule: readonly ScheduledSpeech[],
): DeliveryState {
  switch (action.type) {
    case "PLAY":
      if (schedule.length === 0)
        return { ...state, cursor: 0, status: "complete" };
      return {
        ...state,
        cursor:
          state.status === "complete"
            ? 0
            : boundedCursor(state.cursor, schedule.length),
        status: "playing",
      };
    case "PAUSE":
      return state.status === "playing"
        ? { ...state, status: "paused" }
        : state;
    case "FINISH_ENTRY": {
      if (schedule.length === 0)
        return { ...state, cursor: 0, status: "complete" };
      const spoken = boundedCursor(state.cursor, schedule.length);
      const next = spoken + 1;
      return {
        ...state,
        cursor: next,
        lastSpokenIndex: spoken,
        status: next >= schedule.length ? "complete" : "playing",
      };
    }
    case "STOP":
      return {
        ...state,
        cursor: paragraphStart(schedule, state.cursor),
        lastSpokenIndex: null,
        status: "idle",
      };
    case "PREVIOUS":
      return {
        ...state,
        cursor: adjacentItem(schedule, state.cursor, -1),
        lastSpokenIndex: null,
        status: "idle",
      };
    case "NEXT":
      return {
        ...state,
        cursor: adjacentItem(schedule, state.cursor, 1),
        lastSpokenIndex: null,
        status: "idle",
      };
    case "JUMP": {
      const starts = schedule
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => entry.kind === "paragraph-heading");
      const target = starts[action.paragraph - 1];
      return target === undefined
        ? state
        : {
            ...state,
            cursor: target.index,
            lastSpokenIndex: null,
            status: "idle",
          };
    }
    case "NUDGE_RATE":
      return {
        ...state,
        targetWpm: Math.max(80, Math.min(180, state.targetWpm + action.delta)),
      };
  }
}

export function deliveryDisplayNodes(
  tree: OrdersParseTree,
): readonly DeliveryDisplayNode[] {
  return tree.paragraphs.flatMap((paragraph) => [
    {
      id: paragraph.id,
      kind: "paragraph-heading" as const,
      text: paragraph.displayHeading,
    },
    ...paragraph.items.map((item) => ({
      id: item.id,
      kind: "item" as const,
      text: item.displayText,
    })),
    ...paragraph.subparagraphs.flatMap((subparagraph) => [
      {
        id: subparagraph.id,
        kind: "subparagraph-heading" as const,
        text: subparagraph.displayHeading,
      },
      ...subparagraph.items.map((item) => ({
        id: item.id,
        kind: "item" as const,
        text: item.displayText,
      })),
    ]),
  ]);
}

export function scheduleNodeId(scheduleId: string): string {
  return scheduleId.replace(/\/speech-\d+(?:\/repeat-02)?$/, "");
}

export function deliveryDisplayStates(
  nodes: readonly DeliveryDisplayNode[],
  schedule: readonly ScheduledSpeech[],
  state: DeliveryState,
): readonly DeliveryDisplayState[] {
  const current = schedule[state.cursor];
  const currentId = current === undefined ? null : scheduleNodeId(current.id);
  const firstIndexOf = new Map<string, number>();
  schedule.forEach((entry, index) => {
    const id = scheduleNodeId(entry.id);
    if (!firstIndexOf.has(id)) firstIndexOf.set(id, index);
  });
  // The last display node that has something to say, as the walk passes it.
  let lastSpeaking = -1;
  return nodes.map((node) => {
    const index = firstIndexOf.get(node.id);
    if (index !== undefined) lastSpeaking = index;
    const spoken = index !== undefined && index < state.cursor;
    // A node with nothing to say has no segment, so the cursor never lands on
    // it and it used to stay faint for the whole run — a line of dashes at a
    // quarter opacity between delivered lines, looking skipped. Under Ruling
    // 82 a separator run says nothing, and the pane keeps it because the pane
    // is faithful to the source, so it is delivered once the delivery has
    // passed the node before it. Ruled 17 October 2026 (a), section 11 item 2.
    const passed =
      index === undefined && lastSpeaking >= 0 && lastSpeaking < state.cursor;
    return {
      ...node,
      state:
        node.id === currentId
          ? "current"
          : spoken || passed || state.status === "complete"
            ? "delivered"
            : "undelivered",
    };
  });
}

export function sayAgainEntry(
  schedule: readonly ScheduledSpeech[],
  state: DeliveryState,
): ScheduledSpeech | null {
  if (state.lastSpokenIndex === null) return null;
  for (let index = state.lastSpokenIndex; index >= 0; index -= 1) {
    const entry = schedule[index];
    if (
      entry === undefined ||
      (entry.kind !== "item" && entry.kind !== "chunk")
    )
      continue;
    return {
      ...entry,
      id: `${entry.id}/say-again`,
      zone: "critical",
      rate:
        entry.zone === "critical" || entry.zone === "mission"
          ? entry.rate
          : entry.rate * 0.85,
      gapBeforeMs: 0,
      gapAfterMs: 0,
      markerBefore: false,
    };
  }
  return null;
}

export function deliveryActionForKey(
  key: string,
): DeliveryKeyboardAction | "SAY_AGAIN" | null {
  if (key === " ") return { type: "PLAY" };
  if (key === "Escape") return { type: "STOP" };
  if (key === "ArrowLeft") return { type: "PREVIOUS" };
  if (key === "ArrowRight") return { type: "NEXT" };
  if (key === "ArrowUp") return { type: "NUDGE_RATE", delta: 5 };
  if (key === "ArrowDown") return { type: "NUDGE_RATE", delta: -5 };
  if (/^[1-5]$/.test(key)) return { type: "JUMP", paragraph: Number(key) };
  return key.toLowerCase() === "r" ? "SAY_AGAIN" : null;
}

/**
 * Cue firing during delivery. R1 Step 13, released 12 September 2026.
 *
 * Doc 03 section 6.1 fixes the sequence: the general scheme of manoeuvre is
 * written down, `EYES_UP` calls "pens down, eyes up" at the start of the
 * detailed walk, and at the main effort the pens come back up with no verbal
 * call. Doc 04 section 1.2 gives `EYES_UP` a single tone and a banner and
 * makes the resume a silent visual transition.
 */
export type CueName = "EYES_UP" | "PENS_RESUME";

export interface DeliveryCueBinding {
  readonly name: CueName;
  readonly nodeId: string | null;
}

/** The cue bound to a node, or null when the node carries none. */
export function cueAtNode(
  bindings: readonly DeliveryCueBinding[],
  nodeId: string | null,
): CueName | null {
  if (nodeId === null) return null;
  return bindings.find((binding) => binding.nodeId === nodeId)?.name ?? null;
}

/**
 * Whether pens are down after a cue fires. `EYES_UP` puts them down and
 * `PENS_RESUME` brings them back up; any other node leaves the state alone.
 */
export function pensDownAfter(current: boolean, cue: CueName | null): boolean {
  if (cue === "EYES_UP") return true;
  if (cue === "PENS_RESUME") return false;
  return current;
}
